import { useState } from 'react';
import { useCreateRole, useAvailablePermissions } from '../hooks/useRoles';
import { useConfirm, withConfirmSuccess } from '@/shared/contexts/confirm-context';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field, FieldLabel } from '@/components/ui/field';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Plus } from 'lucide-react';

export const CreateRoleDialog = () => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const createMutation = useCreateRole();
  const confirm = useConfirm();
  const permissionsQuery = useAvailablePermissions();

  const handlePermissionToggle = (permissionName: string) => {
    setSelectedPermissions((prev) =>
      prev.includes(permissionName)
        ? prev.filter((p) => p !== permissionName)
        : [...prev, permissionName]
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});

    confirm({
      title: "Create role?",
      description: `“${name}” will be created with ${selectedPermissions.length} permission(s).`,
      confirmLabel: "Create role",
      onConfirm: () =>
        withConfirmSuccess(
          createMutation.mutateAsync({
            name,
            description,
            permissionNames: selectedPermissions,
          }),
          `Role “${name}” created`
        )
          .then(() => {
            setName('');
            setDescription('');
            setSelectedPermissions([]);
            setOpen(false);
          })
          .catch((error: any) => {
            if (error.response?.data?.errors) {
              setFieldErrors(error.response.data.errors);
            }
            throw error;
          }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2">
          <Plus className="w-4 h-4" />
          New Role
        </Button>
      </DialogTrigger>
      <DialogContent 
        className="max-h-[90vh] overflow-y-auto overflow-x-hidden [&>*]:min-w-0 w-[95vw] sm:!max-w-[1400px]">
        <DialogHeader>
          <DialogTitle>Create New Role</DialogTitle>
          <DialogDescription>
            Create a new role and assign permissions
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {createMutation.error && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">
              {createMutation.error.message}
            </div>
          )}

          <Field>
            <FieldLabel htmlFor="name">
              Role Name <span className="text-red-500">*</span>
            </FieldLabel>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={createMutation.isPending}
            />
            {fieldErrors.name && (
              <div className="text-red-500 text-xs mt-1">
                {fieldErrors.name.join(', ')}
              </div>
            )}
          </Field>

          <Field>
            <FieldLabel htmlFor="description">
              Description <span className="text-red-500">*</span>
            </FieldLabel>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              disabled={createMutation.isPending}
              placeholder="Describe this role"
              className="flex min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
            />
            {fieldErrors.description && (
              <div className="text-red-500 text-xs mt-1">
                {fieldErrors.description.join(', ')}
              </div>
            )}
          </Field>

          <div>
            <Label className="text-base font-semibold mb-4 block">
              Permissions <span className="text-red-500">*</span>
            </Label>
            <div className="space-y-3 border rounded-lg p-4">
              {permissionsQuery.data?.map((permission) => (
                <div key={permission.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={permission.id}
                    checked={selectedPermissions.includes(permission.name)}
                    onCheckedChange={() =>
                      handlePermissionToggle(permission.name)
                    }
                    disabled={createMutation.isPending}
                  />
                  <Label
                    htmlFor={permission.id}
                    className="cursor-pointer flex-1"
                  >
                    <div className="font-medium">{permission.name}</div>
                    <div className="text-xs text-gray-500">
                      {permission.description}
                    </div>
                  </Label>
                </div>
              ))}
            </div>
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={createMutation.isPending}
          >
            {createMutation.isPending ? 'Creating...' : 'Create Role'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};